import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage, api } from '../context/AppContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Footprints, Check, X, Loader2 } from 'lucide-react';

const GOAL = 10000;

function CircleProgress({ steps, size = 120, strokeWidth = 8 }) {
  const pct = Math.min(100, (steps / GOAL) * 100);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  const goalReached = steps >= GOAL;
  // Yuri spec 2026-02 (final neon phase): violet electric glow on the progress
  // circle to balance the orange + blue palette already on the dashboard.
  // When the goal is reached, the ring shifts to the orange-fluo signature so
  // the milestone still feels triumphant.
  const VIOLET_GLOW = '0 0 14px rgba(168,85,247,0.65), 0 0 5px rgba(147,51,234,0.5)';
  const ORANGE_GLOW = '0 0 16px rgba(251,146,60,0.7), 0 0 6px rgba(245,158,11,0.55)';
  const ringFilter = goalReached ? `drop-shadow(${ORANGE_GLOW})` : `drop-shadow(${VIOLET_GLOW})`;

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size, filter: ringFilter }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <defs>
          <linearGradient id="steps-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={goalReached ? '#f59e0b' : '#a855f7'} />
            <stop offset="100%" stopColor={goalReached ? '#f97316' : '#7c3aed'} />
          </linearGradient>
        </defs>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="currentColor" strokeWidth={strokeWidth} className="text-white/5" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="url(#steps-grad)" strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={offset} className="transition-all duration-700" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <Footprints className={`w-4 h-4 mb-0.5 ${goalReached ? 'text-amber-400' : 'text-violet-400'}`} />
        <span className="text-lg font-black">{steps >= 1000 ? `${(steps/1000).toFixed(1)}k` : steps}</span>
        <span className="text-[8px] text-muted-foreground uppercase tracking-widest">/ 10k</span>
      </div>
    </div>
  );
}

export default function StepsWidget() {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [steps, setSteps] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showInput, setShowInput] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [xpAwarded, setXpAwarded] = useState(false);

  const fetchSteps = useCallback(async () => {
    try {
      const res = await api.get('/steps/today');
      setSteps(res.data.steps || 0);
    } catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSteps(); }, [fetchSteps]);

  const handleSave = async () => {
    const val = parseInt(inputVal);
    if (isNaN(val) || val < 0) { toast.error(fr ? 'Nombre invalide' : 'Invalid number'); return; }
    setSaving(true);
    try {
      const res = await api.post('/steps/log', { steps: val });
      setSteps(val);
      setShowInput(false);
      setInputVal('');
      if (res.data.xp_awarded > 0) {
        setXpAwarded(true);
        toast.success(fr ? `+${res.data.xp_awarded} XP! Badge Extra Miles!` : `+${res.data.xp_awarded} XP! Extra Miles badge!`);
        setTimeout(() => setXpAwarded(false), 3000);
      } else if (res.data.goal_reached) {
        toast.success(fr ? 'Objectif 10k atteint!' : '10k goal reached!');
      } else {
        toast.success(fr ? 'Pas enregistres!' : 'Steps logged!');
      }
    } catch { toast.error('Erreur'); } finally { setSaving(false); }
  };

  const goalReached = steps >= GOAL;

  if (loading) return null;

  return (
    <Card
      className={`border-0 shadow-lg ${goalReached ? 'bg-gradient-to-br from-amber-500/10 via-card to-orange-500/8' : 'bg-gradient-to-br from-violet-500/8 via-card to-purple-500/4'}`}
      style={{
        boxShadow: goalReached
          ? '0 0 18px rgba(251,146,60,0.5), 0 0 6px rgba(245,158,11,0.4), inset 0 0 0 1px rgba(251,146,60,0.20)'
          : '0 0 16px rgba(168,85,247,0.4), 0 0 5px rgba(147,51,234,0.3), inset 0 0 0 1px rgba(168,85,247,0.15)',
      }}
      data-testid="steps-widget"
    >
      <CardContent className="p-4">
        <Link to="/walking" className="flex items-center gap-4 hover:opacity-90 transition-opacity" data-testid="steps-widget-link">
          <CircleProgress steps={steps} size={100} strokeWidth={7} />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold mb-1">
              {fr ? 'Pas aujourd\'hui' : 'Steps today'}
            </p>
            <p className="text-xl font-black">{steps.toLocaleString()}</p>
            <p className="text-[9px] text-muted-foreground">
              {goalReached
                ? (fr ? 'Objectif atteint!' : 'Goal reached!')
                : (fr ? `${(GOAL - steps).toLocaleString()} pas restants` : `${(GOAL - steps).toLocaleString()} steps remaining`)
              }
            </p>
            <p className="text-[9px] text-primary font-bold uppercase tracking-wider mt-1">
              {fr ? 'Voir Centre de Commandement →' : 'Command Center →'}
            </p>
            <AnimatePresence>
              {xpAwarded && (
                <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="text-[10px] text-amber-400 font-bold mt-1">
                  +100 XP
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Link>

        {/* Quick input */}
        <div className="mt-3">
          <AnimatePresence mode="wait">
            {showInput ? (
              <motion.div key="input" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                className="flex items-center gap-2">
                <Input type="number" placeholder={fr ? 'Nombre de pas' : 'Step count'} value={inputVal} onChange={(e) => setInputVal(e.target.value)}
                  className="flex-1 text-center font-bold" autoFocus onKeyDown={(e) => e.key === 'Enter' && handleSave()} data-testid="steps-input" />
                <Button size="sm" onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white" data-testid="steps-save-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowInput(false)}>
                  <X className="w-3.5 h-3.5" />
                </Button>
              </motion.div>
            ) : (
              <motion.div key="button" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => { setShowInput(true); setInputVal(steps > 0 ? String(steps) : ''); }} data-testid="steps-log-btn">
                  <Footprints className="w-3.5 h-3.5 mr-1.5" />
                  {steps > 0 ? (fr ? 'Mettre a jour mes pas' : 'Update my steps') : (fr ? 'Entrer mes pas' : 'Log my steps')}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </CardContent>
    </Card>
  );
}
